import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import { CurrentContextService } from '../../core/context/current-context.service';
import { liveStatusTranslationKey } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InventoryPageHintComponent } from './inventory-page-hint.component';
import { CatalogOffering, CatalogService, CatalogWarehouse, CatalogWarehouseBin, InventoryReservation } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-inventory-reservations',
  imports: [
    AinPageComponent, AinStateComponent, InventoryPageHintComponent, FormsModule, ButtonModule,
    InputTextModule, SelectModule, TableModule, DialogModule, AinCanAccessDirective, RouterLink
  ],
  template: `
    <ain-page [title]="t('catalog.outbound.reservationsTitle')" [description]="t('catalog.outbound.reservationsDescription')">
      <ain-inventory-page-hint pageKey="reservations" />
      <span class="live-status" [class]="liveUpdateService.connectionState()">{{ t(liveStatusTranslationKey(liveUpdateService.connectionState())) }}</span>
      @if (loading()) { <ain-state type="loading" [title]="t('catalog.loading')" /> }
      @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('catalog.inventory.states.noPermission')" [message]="t('catalog.inventory.states.noPermissionMessage')" />
      } @else {
        <section class="card">
          <h3>{{ t('catalog.outbound.createReservation') }}</h3>
          <div class="grid">
            <label><span>{{ t('catalog.inventory.offeringId') }}</span><p-select [(ngModel)]="form.offeringId" [options]="offeringOptions()" optionLabel="label" optionValue="value" appendTo="body" [filter]="true" /></label>
            <label><span>{{ t('catalog.inventory.warehouseId') }}</span><p-select [(ngModel)]="form.warehouseId" [options]="warehouseOptions()" optionLabel="label" optionValue="value" appendTo="body" (ngModelChange)="loadBins()" /></label>
            <label><span>{{ t('catalog.inbound.targetBin') }}</span><p-select [(ngModel)]="form.binId" [options]="binOptions()" optionLabel="label" optionValue="value" appendTo="body" /></label>
            <label><span>{{ t('catalog.outbound.sourceReference') }}</span><input pInputText [(ngModel)]="form.sourceReference" /></label>
            <label><span>{{ t('catalog.inventory.quantity') }}</span><input pInputText type="number" [(ngModel)]="form.quantity" /></label>
          </div>
          <button pButton type="button" ainCanAccess [ainCanAccessPermission]="'inventory.reservations.create'" [label]="t('catalog.action.create')" (click)="create()"></button>
          @if (availability()) {
            <p class="hint">{{ t('catalog.outbound.availability') }}: {{ availability()!.availableQuantity }} / {{ availability()!.onHandQuantity }}</p>
          }
        </section>

        <div class="toolbar">
          <button pButton type="button" severity="secondary" ainCanAccess [ainCanAccessPermission]="'inventory.reservations.create'" [label]="t('catalog.pickWaves.createFromSelection')" [disabled]="selectedRows.length === 0" (click)="openPickWaveDialog()"></button>
          <a pButton routerLink="/inventory/pick-waves" severity="secondary" [label]="t('nav.inventory.pickWaves')"></a>
        </div>

        <p-table [value]="reservations()" [(selection)]="selectedRows" dataKey="id">
          <ng-template pTemplate="header">
            <tr>
              <th style="width: 44px"><p-tableHeaderCheckbox /></th>
              <th>{{ t('catalog.outbound.sourceReference') }}</th><th>{{ t('columns.status') }}</th><th>{{ t('catalog.inventory.quantity') }}</th><th></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td><p-tableCheckbox [value]="row" [disabled]="row.status !== 'Active'" /></td>
              <td>{{ row.sourceType }} — {{ row.sourceReference }}</td><td>{{ row.status }}</td><td>{{ row.quantity }}</td>
              <td>
                @if (row.status === 'Active') {
                  <button pButton type="button" size="small" ainCanAccess [ainCanAccessPermission]="'inventory.tasks.execute'" [label]="t('catalog.outbound.createPick')" (click)="createPick(row)"></button>
                  <button pButton type="button" size="small" severity="secondary" ainCanAccess [ainCanAccessPermission]="'inventory.reservations.create'" [label]="t('catalog.outbound.release')" (click)="release(row)"></button>
                }
              </td>
            </tr>
          </ng-template>
        </p-table>
      }

      <p-dialog [header]="t('catalog.pickWaves.createTitle')" [visible]="pickWaveDialogOpen()" [modal]="true" [style]="{ width: '28rem' }" (visibleChange)="pickWaveDialogOpen.set($event)">
        <label class="dialog-field">
          <span>{{ t('catalog.pickWaves.referenceNumber') }}</span>
          <input pInputText [(ngModel)]="pickWaveReference" />
        </label>
        <p class="hint">{{ t('catalog.pickWaves.selectedCount') }}: {{ selectedRows.length }}</p>
        <div class="dialog-footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="pickWaveDialogOpen.set(false)"></button>
          <button pButton type="button" [label]="t('catalog.action.create')" (click)="createPickWave()" [disabled]="!pickWaveReference.trim() || pickWaveBusy()"></button>
        </div>
      </p-dialog>
    </ain-page>
  `,
  styles: ['.card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; margin-bottom: 12px; } .grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px,1fr)); gap: 8px; } .hint { margin-top: 8px; color: #475569; } .toolbar { display: flex; gap: 8px; margin-bottom: 10px; flex-wrap: wrap; } .live-status { font-size:0.8rem;color:#6b7280;display:block;margin-bottom:8px; } .live-status.connected{color:#166534;} .dialog-field { display: grid; gap: 6px; margin-bottom: 8px; } .dialog-footer { display: flex; gap: 8px; justify-content: flex-end; margin-top: 12px; }']
})
export class InventoryReservationsComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusTranslationKey = liveStatusTranslationKey;
  readonly loading = signal(true);
  readonly permissionDenied = signal(false);
  readonly pickWaveDialogOpen = signal(false);
  readonly pickWaveBusy = signal(false);
  readonly reservations = signal<InventoryReservation[]>([]);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly warehouses = signal<CatalogWarehouse[]>([]);
  readonly bins = signal<CatalogWarehouseBin[]>([]);
  readonly availability = signal<{ onHandQuantity: number; availableQuantity: number } | null>(null);
  selectedRows: InventoryReservation[] = [];
  pickWaveReference = '';
  readonly form = { offeringId: '', warehouseId: '', binId: '', sourceReference: '', quantity: 0 };
  t = (k: string) => this.localization.translate(k);
  offeringOptions = () => this.offerings().filter(o => o.inventoryEnabled).map(o => ({ label: o.code, value: o.id }));
  warehouseOptions = () => this.warehouses().map(w => ({ label: w.code, value: w.id }));
  binOptions = () => this.bins().map(b => ({ label: b.code, value: b.id }));

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => this.reload()
    );
    this.service.listOfferings().subscribe({ next: o => this.offerings.set(o.filter(x => x.inventoryEnabled)) });
    this.service.listWarehouses().subscribe({ next: w => this.warehouses.set(w) });
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.reservationIdFilter = params.get('reservationId') ?? '';
      this.reload();
    });
    this.reload();
  }

  private reservationIdFilter = '';

  loadBins(): void {
    if (!this.form.warehouseId) return;
    this.service.listWarehouseBins(this.form.warehouseId).subscribe({ next: b => this.bins.set(b) });
  }

  create(): void {
    this.service.createInventoryReservation({
      offeringId: this.form.offeringId,
      warehouseId: this.form.warehouseId,
      binId: this.form.binId || null,
      sourceType: 'Manual',
      sourceReference: this.form.sourceReference,
      quantity: this.form.quantity
    }).subscribe({ next: () => this.reload() });
  }

  createPick(row: InventoryReservation): void {
    this.service.createPickTaskFromReservation(row.id).subscribe({ next: () => this.reload() });
  }

  release(row: InventoryReservation): void {
    this.service.releaseInventoryReservation(row.id).subscribe({ next: () => this.reload() });
  }

  openPickWaveDialog(): void {
    this.pickWaveReference = `WAVE-${Date.now()}`;
    this.pickWaveDialogOpen.set(true);
  }

  createPickWave(): void {
    const ids = this.selectedRows.map(r => r.id);
    if (!ids.length) return;
    this.pickWaveBusy.set(true);
    this.service.createPickWave(this.pickWaveReference.trim(), ids).subscribe({
      next: wave => {
        this.pickWaveBusy.set(false);
        this.pickWaveDialogOpen.set(false);
        this.selectedRows = [];
        void this.router.navigate(['/inventory/pick-waves', wave.id]);
      },
      error: () => this.pickWaveBusy.set(false)
    });
  }

  reload(): void {
    this.service.listInventoryReservations(undefined, this.reservationIdFilter || undefined).subscribe({
      next: list => { this.reservations.set(list); this.permissionDenied.set(false); this.loading.set(false); },
      error: err => {
        this.permissionDenied.set(err?.status === 403);
        this.loading.set(false);
      }
    });
    if (this.form.offeringId && this.form.warehouseId) {
      this.service.getInventoryAvailability(this.form.offeringId, this.form.warehouseId, this.form.binId || null)
        .subscribe({ next: a => this.availability.set(a) });
    }
  }
}
